import merge from 'lodash/merge';
import * as $ from 'jquery';
import { parseJson, stringifyJson } from 'core/utils';
import { isMoment, moment2unix, moment2text } from 'core/utils';

const MOMENT_TO_UNIX = false;

// =============================================================================

function debug(...args) {
  //console.log(...args);
}

// =============================================================================

function toQueryString(arg, prefix) {
  if (arg === null || arg === undefined) {
    return null;
  }
  const type = typeof arg;
  if (type === 'string') {
    const val = encodeURIComponent(arg);
    return prefix
        ? `${prefix}=${val}`
        : val
        ;
  } else if (type === 'number') {
    const val = arg;
    return prefix
        ? `${prefix}=${val}`
        : val
        ;
  } else if (type === 'boolean') {
    const val = arg ? 'true' : 'false';
    return prefix
        ? `${prefix}=${val}`
        : val
        ;
  } else if (isMoment(arg)) {
    const val = MOMENT_TO_UNIX ? moment2unix(arg) : moment2text(arg, 'YYYY-MM-DD');
    return prefix
        ? `${prefix}=${val}`
        : val
        ;
  } else if (Array.isArray(arg)) {
    const urlParameters = [];
    const basePrefix = prefix
        ? `${prefix}`
        : ''
        ;
    for (let i = 0; i < arg.length; i++) {
      const param = toQueryString(arg[i], `${basePrefix}`);
      if (param !== null) {
        urlParameters.push(param);
      }
    }
    return urlParameters.length > 0
        ? urlParameters.join('&')
        : null;
  } else if (type === 'object') {
    const urlParameters = [];
    const basePrefix = prefix
        ? `${prefix}.`
        : ''
        ;
    Object.keys(arg).forEach((key, index) => {
      const param = toQueryString(arg[key], basePrefix + key);
      if (param !== null) {
        urlParameters.push(param);
      }
    });
    return urlParameters.length > 0
        ? urlParameters.join('&')
        : null;
  }
  throw Error(`Unsupported conversion. type = ${type}`);
}

// =============================================================================

import DefaultHttpInterceptor from './DefaultHttpInterceptor';

let interceptor = new DefaultHttpInterceptor();

export function setHttpInterceptor(httpInterceptor) {
  interceptor = httpInterceptor;
}

// -------------------------------------------------------
// Transforming success function
// -------------------------------------------------------

function success(execute, type, url, data, textStatus, jqXHR) {
  const statusCode = jqXHR.status;

  debug(type, url, 'SUCCESS');
  debug('\tjqXHR', jqXHR);
  debug('\ttextStatus', textStatus);
  debug('\tstatus', statusCode);
  debug('\tdata', data);

  return interceptor.handleSuccess({ execute, statusCode, data });
}

// -------------------------------------------------------
// Transforming error function
// -------------------------------------------------------

function error(execute, type, url, jqXHR, textStatus, errorThrown) {
  const statusCode = jqXHR.status;

  let data;
  try {
    data = parseJson(jqXHR.responseText);
  } catch (err) {
    data = jqXHR.responseText;
  }

  debug(type, url, 'ERROR');
  debug('\tjqXHR', jqXHR);
  debug('\ttextStatus', textStatus);
  debug('\terrorThrown', errorThrown);
  debug('\tstatus', statusCode);
  debug('\tresponse', data);

  return interceptor.handleError({ execute, statusCode, data });
}

// =============================================================================

class HttpService {
  /**
   * getOrDelete
   */
  getOrDelete(type, {
      blob,
      contentType,
      headers,
      progress,
      queryParams,
      // timeout,
      url,
      xhrResponseType
    }) {

    // -------------------------------------------------------
    // Defaults
    // -------------------------------------------------------
    if (!contentType) {
      contentType = 'application/json';
    }

    if (contentType.indexOf('charset=') < 0) {
      contentType += '; charset=UTF-8';
    }

    // -------------------------------------------------------
    // Handle query parameters
    // -------------------------------------------------------

    debug(type, url, 'QUERY PARAMETERS', queryParams);

    let params = null;
    if (queryParams) {
      if (Array.isArray(queryParams)) {
        params = queryParams;
      } else {
        params = [queryParams];
      }

      params = params
        .filter(x => x !== null && x !== undefined) // Ignore null values
        .map(x => (x.toJs ? x.toJs() : x)); // Call toJs on objects that implement it

      params = merge({}, ...params);
    }

    params = toQueryString(params);
    debug('\tTransformed query parameters', params);

    // -------------------------------------------------------
    // Progress
    // -------------------------------------------------------
    function xhr() {
      var xhr = $.ajaxSettings.xhr();
      if (progress) {
        xhr.onprogress = function (e) {
          // For downloads
          if (e.lengthComputable) {
              progress(e.loaded, e.total);
          }
        };
      }
      if (blob) {
        xhr.onreadystatechange = function() {
          if (xhr.readyState == 2 && xhr.status == 200) {
            xhr.responseType = "blob";
          }
        };
      }
      return xhr;
    }

    // -------------------------------------------------------
    // Ajax call options
    // -------------------------------------------------------
    const options = {
      cache: false,
      contentType,
      crossDomain: true,
      data: params,
      headers,
      // timeout: timeout || 10000, // 10 seconds
      type,
      xhrFields: {
        responseType: xhrResponseType,
        withCredentials: true
      },
      xhr
    };

    // -------------------------------------------------------
    // Execute function
    // -------------------------------------------------------

    function execute() {
      return $.ajax(url, interceptor.transform(options))
        .then(
          (data, textStatus, jqXHR) => success(execute, type, url, data, textStatus, jqXHR),
          (jqXHR, textStatus, errorThrown) => error(execute, type, url, jqXHR, textStatus, errorThrown)
        )
      ;
    }

    // -------------------------------------------------------
    // Ajax call
    // -------------------------------------------------------

    return execute();
  }

  /**
   * postOrPut
   * - progress(loaded, total)
   */
  postOrPut(type, {
      blob,
      contentType,
      data,
      headers,
      processData,
      progress,
      // timeout,
      url
    }) {

    // -------------------------------------------------------
    // Defaults
    // -------------------------------------------------------
    if (!contentType) {
      contentType = 'application/json';
    }

    if (contentType.indexOf('charset=') < 0) {
      contentType += '; charset=UTF-8';
    }

    debug(type, url, 'BODY', data);

    if (contentType.startsWith('application/json')) {
      data = stringifyJson(data);
      debug('\tJSON', data);
    }

    // -------------------------------------------------------
    // Progress
    // -------------------------------------------------------
    function xhr() {
      var xhr = $.ajaxSettings.xhr();
      if (progress) {
        xhr.upload.onprogress = function (e) {
          // For uploads
          if (e.lengthComputable) {
              progress(e.loaded, e.total);
          }
        };
      }
      if (blob) {
        xhr.onreadystatechange = function() {
          if (xhr.readyState == 2 && xhr.status == 200) {
            xhr.responseType = "blob";
          }
        };
      }
      return xhr;
    }

    // -------------------------------------------------------
    // Ajax call options
    // -------------------------------------------------------
    const options = {
      contentType,
      crossDomain: true,
      data,
      headers,
      processData,
      // timeout: timeout || 10000, // 10 seconds
      type,
      xhrFields: {
        withCredentials: true
      },
      xhr
    };

    // -------------------------------------------------------
    // Execute function
    // -------------------------------------------------------

    function execute() {
      return $.ajax(url, interceptor.transform(options))
        .then(
          (data, textStatus, jqXHR) => success(execute, type, url, data, textStatus, jqXHR),
          (jqXHR, textStatus, errorThrown) => error(execute, type, url, jqXHR, textStatus, errorThrown)
        )
      ;
    }

    // -------------------------------------------------------
    // Ajax call
    // -------------------------------------------------------

    return execute();
  }

  get(args) {
    return this.getOrDelete('GET', args);
  }

  delete(args) {
    return this.getOrDelete('DELETE', args);
  }

  post(args) {
    return this.postOrPut('POST', args);
  }

  put(args) {
    return this.postOrPut('PUT', args);
  }

  upload(args) {
    const file = args.data;

    let headers = args.headers || {};
    delete args.headers;

    headers = {
      'file.name': file.name,
      'file.size': file.size,
      'file.contentType': file.type,
      ...headers
    };

    console.log('Uploading file with headers', headers);

    return this.post({
      contentType: 'application/octet-stream',
      processData: false,
      headers,
      ...args
    })
  }
}

const service = new HttpService();

export default service;
